import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/utility/migration/chevron-up';
import { Box, xcss, Flex, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createContainer,
	createHook,
	createStore,
} from '@atlassian/react-sweet-state';
import messages from './messages.tsx';

export const AccordionContainer = createContainer<{ initial: AccordionState }>();
export type AccordionState = Record<
	string,
	{
		isOpen: boolean;
	}
>;
const AccordionStore = createStore({
	containedBy: AccordionContainer,
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	initialState: {} as AccordionState,
	actions: {
		open:
			(id: string) =>
			({ setState }) => {
				setState({ [id]: { isOpen: true } });
			},
		close:
			(id: string) =>
			({ setState }) => {
				setState({ [id]: { isOpen: false } });
			},
	},
	handlers: {
		onInit:
			() =>
			({ setState }, { initial }: { initial: AccordionState }) => {
				setState(initial);
			},
	},
});

const getAccordionFromId = (state: AccordionState, id: string) => state[id] || { isOpen: false };
export const useAccordion = createHook(AccordionStore, {
	selector: getAccordionFromId,
});
export const useAccordions = createActionsHook(AccordionStore);

export const Accordion = ({
	children,
	projectName,
	projectAvatar,
	id,
}: React.PropsWithChildren<{
	projectName: string;
	projectAvatar: string;
	id: string;
}>) => {
	const [state, { open, close }] = useAccordion(id);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onToggle = useCallback(
		(event: React.SyntheticEvent<HTMLDetailsElement, Event>) => {
			if (event.currentTarget.open && !state.isOpen) {
				open(id);
			} else if (!event.currentTarget.open && state.isOpen) {
				close(id);
			}
		},
		[open, id, state.isOpen, close],
	);
	const { isOpen } = state;
	const onClick = useCallback(() => {
		if (!isOpen) {
			fireUIAnalytics(createAnalyticsEvent({}), 'accordion toggled', 'projectAccordionExpanded');
		} else {
			fireUIAnalytics(createAnalyticsEvent({}), 'accordion toggled', 'projectAccordionCollapsed');
		}
	}, [isOpen, createAnalyticsEvent]);
	const { formatMessage } = useIntl();

	const Chevron = isOpen ? ChevronUpIcon : ChevronDownIcon;
	return (
		<AccordionWrapper
			data-testid={`personal-settings-notifications-config-table.common.ui.accordion.accordion-wrapper-${id}`}
			onToggle={onToggle}
			open={isOpen}
		>
			<AccordionHeader
				// event is on click so that it isn't fired when opened from the search box
				onClick={onClick}
				data-testid={`personal-settings-notifications-config-table.common.ui.accordion.accordion-header-${id}`}
			>
				<Flex gap="space.100" alignItems="center">
					<Chevron color={token('color.icon')} LEGACY_primaryColor={token('color.icon')} label="" />
					<Flex gap="space.100">
						<Text weight="medium" color="color.text.subtle">
							{formatMessage(messages.notificationColumnHeaderPrefix)}
						</Text>
						<Avatar
							borderColor="transparent"
							size="xsmall"
							appearance="square"
							src={projectAvatar}
						/>
						<Heading size="small" color="color.text">
							{projectName}
						</Heading>
					</Flex>
				</Flex>
			</AccordionHeader>
			<Box xcss={accordionBodyStyles}>{children}</Box>
		</AccordionWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AccordionWrapper = styled.details<{ open: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: (props) =>
		props?.open ? token('color.background.selected') : token('elevation.surface.sunken'),
	borderRadius: token('space.050'),
});

const accordionBodyStyles = xcss({
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	paddingBottom: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AccordionHeader = styled.summary({
	display: 'flex',
	alignItems: 'center',
	userSelect: 'none',
	color: 'inherit',
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
	margin: 0,
	cursor: 'pointer',
	borderRadius: token('space.050'),
	borderStyle: 'none',
	listStyle: 'none',
	transition: 'background 0.4s',
});
